"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { HeroBody } from "./HeroBody";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

export function AuroraHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const blobA = useRef<HTMLDivElement | null>(null);
  const blobB = useRef<HTMLDivElement | null>(null);
  const blobC = useRef<HTMLDivElement | null>(null);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!rootRef.current) return;
    const ctx = gsap.context(() => {
      const targets = [badgeRef.current, titleRef.current, subtitleRef.current, ctasRef.current].filter(Boolean);
      if (reduced) {
        gsap.set(targets, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(targets, { opacity: 0, y: 28 });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.12,
        delay: 0.1,
      });

      // Floating aurora blobs
      [blobA, blobB, blobC].forEach((r, i) => {
        if (!r.current) return;
        gsap.to(r.current, {
          xPercent: i % 2 === 0 ? 8 : -10,
          yPercent: i === 1 ? -6 : 8,
          scale: 1.08,
          duration: 8 + i * 1.5,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });
    }, rootRef);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={rootRef}
      className="hero-section hero-grain relative isolate overflow-hidden bg-[var(--primary-darker)] px-4 py-20 md:py-28"
      aria-label="Hero"
    >
      <div aria-hidden className="absolute inset-0">
        <div
          ref={blobA}
          className="absolute -left-24 top-[-20%] size-[55rem] rounded-full opacity-70 blur-3xl"
          style={{ background: "radial-gradient(circle at 30% 30%, var(--accent), transparent 58%)" }}
        />
        <div
          ref={blobB}
          className="absolute right-[-15%] top-[10%] size-[48rem] rounded-full opacity-55 blur-3xl"
          style={{ background: "radial-gradient(circle at 60% 50%, var(--primary-light), transparent 62%)" }}
        />
        <div
          ref={blobC}
          className="absolute bottom-[-25%] left-1/3 size-[42rem] rounded-full opacity-45 blur-3xl"
          style={{ background: "radial-gradient(circle at 50% 50%, color-mix(in oklch, var(--accent) 60%, #6366f1), transparent 68%)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 85% 65% at 50% -5%, transparent 0%, rgba(0,0,0,0.5) 100%), linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.35) 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
          aria-hidden
        />
      </div>

      <HeroBody
        content={content}
        primaryVariant="primary"
        secondaryVariant="outline"
        refs={{ badge: badgeRef, title: titleRef, subtitle: subtitleRef, ctas: ctasRef }}
      />
    </section>
  );
}
